import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { HelpOutlineRounded } from "@mui/icons-material";
import classNames from "classnames";
import { capitalizeFirstChar } from "../../helpers";

const ToggleButtonsGroup = ({
  stateVar,
  prefType,
  setter,
  options,
  toolTipContent,
  width,
}: {
  stateVar?: string;
  prefType?: string;
  setter?: (string) => void;
  options?: (string | { label: string; icon?: React.ReactNode })[];
  toolTipContent?: string | null;
  width?: number;
}) => {
  const [selectedOption, setSelectedOption] = useState(stateVar);
  const [highlightStyle, setHighlightStyle] = useState({});
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    setSelectedOption(stateVar);
  }, [stateVar]);

  const handleAlignment = (newAlignment: string | null) => {
    if (newAlignment !== null) {
      setSelectedOption(newAlignment);
      setter?.(newAlignment);
    }
  };

  useLayoutEffect(() => {
  // ensures layout is settled first before applying styles
    const index = options.findIndex(
      option => (typeof option === "string" ? option : option.label) === selectedOption,
    );
    const button = buttonRefs.current[index];
    if (button) {
      const { offsetLeft, offsetWidth } = button;
      setHighlightStyle({
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`,
      });
    }
  }, [selectedOption, options]);

  return (
    <div className="flex flex-col gap-y-2 sm:gap-y-4 sm:flex-row justify-between">
      <div>
        {prefType && (
          <Typography
            variant="body2/regular-400"
            className="text-mui-black-60 dark:text-mui-white-70 pe-1"
          >
            {prefType}
          </Typography>
        )}
        {toolTipContent && (
          <Tooltip
            placement="right"
            title={<p className="text-center text-xs">{toolTipContent}</p>}
            classes={{
              tooltip: "!w-[210px]",
            }}
          >
            <IconButton>
              <HelpOutlineRounded className="text-mui-black-60 dark:text-mui-white-70 opacity-60 !w-4 !h-4" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div
        className={classNames(
          "self-center", { [`w-[${width}]`]: width, "w-[292px] md:w-[340px] lg:w-[462px]": !width },
        )}
      >
        <div className="relative w-full">
          {/* sliding background element will move and resize dynamically depending on the option that is selected  */}
          <div
            className="absolute inset-y-0 transition-transform duration-300 ease-in-out rounded-full bg-tgpt-secondary-states-focus z-0 my-1"
            style={highlightStyle}
          />

          <ToggleButtonGroup
            exclusive
            aria-label="text alignment"
            className="h-[42px] bg-tgpt-secondary-accentLight !rounded-full"
            fullWidth
          >
            {options.map((option, index) => {
              const label = typeof option === "string" ? option : option.label;
              const icon = typeof option === "string" ? null : option.icon;

              return (
                <ToggleButton
                  key={label}
                  value={label}
                  size="medium"
                  className={classNames(
                    "category-button !border-0 hover:!rounded-full !text-[13px]",
                    { "!rounded-full !bg-transparent": selectedOption === label,
                      "flex gap-x-1": icon,
                    },
                  )}
                  selected={selectedOption === label}
                  onClick={() => {
                    handleAlignment(label);
                  }}
                  ref={el => (buttonRefs.current[index] = el)}
                  aria-label="left aligned"
                >
                  {icon}
                  {capitalizeFirstChar(label)}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </div>
      </div>
    </div>
  );
};

export default ToggleButtonsGroup;
